import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../api/auth';
import { tournamentsApi } from '../api/tournaments';
import Logo from './Logo';
import '../styles/home-gaming-new.css';
import { useLanguage } from '../i18n/LanguageContext';
import { sortGameweekTournamentsByRarity } from '../utils/gameweekTournamentOrder';
import { useSound } from '../context/SoundContext';

function formatCountdown(ms) {
  if (ms <= 0) return null;
  const sec = Math.floor(ms / 1000) % 60;
  const min = Math.floor(ms / 60000) % 60;
  const hr = Math.floor(ms / 3600000) % 24;
  const day = Math.floor(ms / 86400000);
  const parts = [];
  if (day > 0) parts.push(`${day}d`);
  if (hr > 0 || parts.length) parts.push(`${hr}h`);
  if (min > 0 || parts.length) parts.push(`${min}m`);
  parts.push(`${sec}s`);
  return parts.join(' ');
}

const Home = () => {
  const { playSound } = useSound();
  const [user, setUser] = useState(null);
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const isLoadingRef = useRef(false);
  const { t } = useLanguage();

  const loadActiveTournaments = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      const response = await tournamentsApi.getActive({ type: 'gameweek' });
      if (response?.success) {
        const gameweeks = response.tournaments || [];
        const groupedGameweeks = gameweeks.reduce((acc, gw) => {
          const nameParts = gw.name.split(' - ');
          const gameweekName = nameParts.length >= 2 
            ? nameParts.slice(0, -1).join(' - ') 
            : nameParts[0];
          
          if (!acc[gameweekName]) {
            acc[gameweekName] = {
              ...gw,
              gameweekName,
              tournaments: [],
              start_date: gw.start_date,
              end_date: gw.end_date,
              status: gw.status
            };
          }
          acc[gameweekName].tournaments.push(gw);
          
          const statuses = acc[gameweekName].tournaments.map(t => t.status);
          if (statuses.includes('in_progress')) {
            acc[gameweekName].status = 'in_progress';
          } else if (statuses.includes('open')) {
            acc[gameweekName].status = 'open';
          } else if (statuses.includes('upcoming')) {
            acc[gameweekName].status = 'upcoming';
          }
          
          return acc;
        }, {});
        
        const validGameweeks = Object.values(groupedGameweeks)
          .filter((gw) => gw.tournaments.length > 0)
          .map((gw) => ({
            ...gw,
            tournaments: sortGameweekTournamentsByRarity(gw.tournaments),
          }))
          .sort((a, b) => {
            const ta = new Date(a.start_date || 0).getTime();
            const tb = new Date(b.start_date || 0).getTime();
            if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
            if (Number.isNaN(ta)) return 1;
            if (Number.isNaN(tb)) return -1;
            return tb - ta;
          });
        setActiveTournaments(validGameweeks);
      }
    } catch (error) {
      console.error('[Home] Error loading tournaments:', error);
      setActiveTournaments([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const currentUser = authApi.getCurrentUser();
    setUser(currentUser);
    loadActiveTournaments();
  }, [loadActiveTournaments]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const nextUpcomingGameweek = useMemo(() => {
    return activeTournaments
      .filter((gameweek) => {
        const status = String(gameweek.status || '').toLowerCase();
        const startTs = new Date(gameweek.start_date).getTime();
        return status === 'upcoming' && !Number.isNaN(startTs) && startTs >= now;
      })
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())[0] || null;
  }, [activeTournaments, now]);

  const nextUpcomingCountdown = useMemo(() => {
    if (!nextUpcomingGameweek?.start_date) return null;
    const ms = new Date(nextUpcomingGameweek.start_date).getTime() - now;
    return formatCountdown(ms);
  }, [nextUpcomingGameweek, now]);

  // Game modes like in the reference image
  const gameModes = [
    {
      id: 'marketplace',
      name: t('home.marketplace'),
      icon: '/images/icon_market.png',
      description: t('home.marketplaceDesc'),
      href: '/marketplace',
      color: '#0066CC'
    },
    {
      id: 'tournaments',
      name: t('home.tournaments'),
      icon: '/images/icon_ball.png',
      description: t('home.tournamentsDesc'),
      href: '/tournaments',
      color: '#E31E24',
      featured: true
    },
    {
      id: 'rankings',
      name: t('home.rankings'),
      icon: '/images/icon_cup.png',
      description: t('home.rankingsDesc'),
      href: '/rankings',
      color: '#00A859'
    }
  ];

  return (
    <div className="gaming-home">
      {/* Stadium Background */}
      <div 
        className="stadium-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      {/* Main Content */}
      <div className="gaming-home-content">
        {/* Game Mode Panels */}
        <div className="game-modes-container">
          {gameModes.map((mode, index) => (
            <Link
              key={mode.id}
              to={mode.href}
              onClick={() => playSound('click')}
              className={`game-mode-panel ${mode.featured ? 'featured' : ''}`}
              style={{ '--panel-color': mode.color }}
              data-tutorial={mode.id === 'marketplace' ? 'nav-marketplace' : mode.id === 'tournaments' ? 'nav-tournaments' : mode.id === 'rankings' ? 'nav-rankings' : null}
            >
              <div className="game-mode-icon">
                <img
                  src={mode.icon}
                  alt={`${mode.name} icon`}
                  className="game-mode-icon-img"
                />
              </div>
              {mode.featured && (
                <div className="game-mode-featured-badge">
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                </div>
              )}
              <div className="game-mode-banner">{mode.name}</div>
              <div className="game-mode-description">{mode.description}</div>
            </Link>
          ))}
        </div>

        {/* Active GameWeeks Section */}
        {!loading && nextUpcomingGameweek && (
          <div className="active-gameweeks-section" data-tutorial="tournaments">
            <h2 className="section-title">{t('home.activeGameweeks')}</h2>
            <div className="gameweeks-grid">
              <Link
                key={nextUpcomingGameweek.id || nextUpcomingGameweek.gameweekName}
                to="/tournaments"
                onClick={() => playSound('click')}
                className="gameweek-shield"
              >
                <div className="shield-header">
                  <span className="shield-title">{nextUpcomingGameweek.gameweekName || nextUpcomingGameweek.name}</span>
                  <span className="shield-status">{nextUpcomingGameweek.status || 'upcoming'}</span>
                </div>
                <div className="shield-content">
                  <p className="shield-info">
                    {t('home.tournamentsCount', { count: nextUpcomingGameweek.tournaments?.length || 0 })}
                  </p>
                  <p className="shield-info">
                    {t('tournaments.starts')}: {new Date(nextUpcomingGameweek.start_date).toLocaleString()}
                  </p>
                  {nextUpcomingCountdown && (
                    <p className="shield-countdown">
                      {t('tournaments.countdownStartsIn')}: <span>{nextUpcomingCountdown}</span>
                    </p>
                  )}
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
